/**
 * Pure-logic state machine for the dossier tab strip (EN-11 + DR-2 + SR-9).
 *
 * Mirrors `dialogueOverlayLogic.ts`'s pure-logic / Phaser-shell split. The
 * Phaser shell at `dossierTabStrip.ts` handles DOM mount + ARIA tablist
 * semantics + focus management; this module owns the active-tab reducer
 * + visible-tabs filter + keyboard intent mapping.
 *
 * Tab strip serves two surfaces with different tab universes:
 *   - FullSheet (5 tabs): ABOUT / STATS / MOVES / LEARNSET / MATCHUPS
 *   - PokedexScene (2 tabs): this-run / career
 *
 * The reducer generalizes on `T extends string` so both surfaces consume
 * one primitive (PokedexScene migrates in PR 4a STRUCTURAL).
 *
 * Mode-conditional ordering (DR-2 / EN-7): live-context openers (orb-tap,
 * PokedexScene THIS RUN tap on an alive member or dead-mid-run member)
 * open FullSheet on STATS; snapshot-context openers (CAREER tab, SKIPPED
 * tab) open on ABOUT. The caller picks the appropriate `visibleTabs`
 * array — this module does not own kind→context mapping (the FullSheet
 * data discriminator drives that at the call site).
 *
 * EN-11 cap: only the active-tab reducer is pure. Focus/ARIA mutation
 * lives in the Phaser shell + is covered by manual smoke at PR 2 land.
 */

/** Keyboard events the reducer maps to active-tab transitions. WAI-ARIA
 *  tablist "automatic" pattern: arrow keys both move focus AND activate
 *  the new tab; Enter / Space are accepted but no-op (the new tab is
 *  already active from the arrow press). */
export type TabKey =
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'Home'
  | 'End'
  | 'Enter'
  | ' ';

/**
 * Resolve a keyboard event to the next active tab.
 *
 *   ArrowRight    → next tab, wrapping at the end.
 *   ArrowLeft     → previous tab, wrapping at the start.
 *   Home          → first tab.
 *   End           → last tab.
 *   Enter / Space → no-op (the active tab is already activated).
 *
 * Returns `current` unchanged when:
 *   - `current` is not in `visibleTabs` (defensive — caller mismatch).
 *   - `visibleTabs` is empty (defensive).
 *   - The key is one of the accepted-but-no-op events above.
 */
export function reduceTabKey<T extends string>(
  current: T,
  key: TabKey,
  visibleTabs: readonly T[],
): T {
  if (visibleTabs.length === 0) return current;
  const idx = visibleTabs.indexOf(current);
  if (idx === -1) return current;
  switch (key) {
    case 'ArrowRight':
      return visibleTabs[(idx + 1) % visibleTabs.length]!;
    case 'ArrowLeft':
      return visibleTabs[(idx - 1 + visibleTabs.length) % visibleTabs.length]!;
    case 'Home':
      return visibleTabs[0]!;
    case 'End':
      return visibleTabs[visibleTabs.length - 1]!;
    case 'Enter':
    case ' ':
      return current;
  }
}

/**
 * Validate a click target. Used by the shell's click handler — clicking a
 * tab not currently visible (e.g. MATCHUPS hidden by SR-6 because the
 * gen-3 chunk failed to load) is a no-op rather than an error. Returns
 * `target` when valid, `fallback` otherwise. The shell always passes
 * `fallback = current` so an invalid click falls through to "no change".
 */
export function validateTabSwitch<T extends string>(
  target: T,
  fallback: T,
  visibleTabs: readonly T[],
): T {
  return visibleTabs.includes(target) ? target : fallback;
}

/** FullSheet's tab universe. Five tabs total. */
export type FullSheetTab = 'about' | 'stats' | 'moves' | 'learnset' | 'matchups';

/** PokedexScene's tab universe. Two tabs. */
export type PokedexTab = 'this-run' | 'career';

/**
 * Caller context that drives FullSheet tab ORDER (NOT data source — those
 * are orthogonal per EN-4 / EN-7).
 *
 *   'live-context'     — STATS first. Used by PartySheetScene orb-tap,
 *                        PokedexScene THIS RUN tap on an alive member,
 *                        AND THIS RUN tap on a dead-mid-run member
 *                        (career-snapshot data + live tab order).
 *   'snapshot-context' — ABOUT first. Used by PokedexScene CAREER tab,
 *                        SKIPPED tab, post-Reset Run views.
 */
export type DossierContext = 'live-context' | 'snapshot-context';

const LIVE_CONTEXT_ORDER: readonly FullSheetTab[] = [
  'stats', 'about', 'moves', 'learnset', 'matchups',
];

const SNAPSHOT_CONTEXT_ORDER: readonly FullSheetTab[] = [
  'about', 'stats', 'moves', 'learnset', 'matchups',
];

/**
 * Resolve the visibleTabs order for FullSheet given a caller context.
 * `availableTabs` lets the caller hide tabs that failed to load (SR-6
 * graceful degradation; e.g. MATCHUPS hides if @pkmn/dex rejects).
 *
 * The returned array preserves the context's order while filtering out
 * any tab not in `availableTabs`. Default `availableTabs` is "all five".
 */
export function getFullSheetTabOrder(
  context: DossierContext,
  availableTabs: ReadonlySet<FullSheetTab> = new Set<FullSheetTab>([
    'about', 'stats', 'moves', 'learnset', 'matchups',
  ]),
): FullSheetTab[] {
  const ordered = context === 'live-context'
    ? LIVE_CONTEXT_ORDER
    : SNAPSHOT_CONTEXT_ORDER;
  return ordered.filter((t) => availableTabs.has(t));
}

/**
 * Default tab for a FullSheet context (DR-2). Live-context → STATS;
 * snapshot-context → ABOUT. If the preferred default isn't available
 * (degraded by SR-6), falls back to the first visible tab in the
 * context's order. Returns 'about' as the ultimate fallback if
 * `visibleTabs` is empty (defensive — should not occur in practice).
 */
export function getDefaultFullSheetTab(
  context: DossierContext,
  visibleTabs: readonly FullSheetTab[],
): FullSheetTab {
  const preferred: FullSheetTab = context === 'live-context' ? 'stats' : 'about';
  if (visibleTabs.includes(preferred)) return preferred;
  return visibleTabs[0] ?? 'about';
}
